/**
 * Stream instrumentation shared by manually enabled debug sessions and the
 * small production telemetry sample. It is self-contained so collection can
 * be removed in one commit once the baseline is captured.
 *
 * Two questions this answers, per the design doc:
 *   1. Which channel key carries the bytes?
 *   2. Does per-frame cost stay flat while the session ages? If it does, the
 *      lag is accumulation and reducing volume will not fix it.
 *
 * Force-enable locally with `localStorage.setItem('oemsStreamDebug', '1')` and
 * reload. Production sampling is selected separately per browser session.
 */

type ChannelStat = { bytes: number; frames: number }
const MAX_SESSION_SAMPLES = 120

/** Bucket for data frames `extractChannelKeys` routes to no listener at all. */
export const UNROUTED_CHANNEL = '(unrouted)'

type SessionSample = {
  atMs: number
  ageMs: number
  bytesSinceLast: number
  dispatchMsSinceLast: number
  heapBytes: number | null
  longTaskMsSinceLast: number
  listenerCount: number
  framesSinceLast: number
  bulkFramesSinceLast: number
  criticalFramesSinceLast: number
  bulkBytesSinceLast: number
  criticalBytesSinceLast: number
  decodeMsSinceLast: number
  maxQueueLagMsSinceLast: number
  maxSourceAgeMsSinceLast: number
  reconnectScheduledSinceLast: number
  resubscribeScheduledSinceLast: number
  resubscribeRunsSinceLast: number
  serverErrorsSinceLast: number
  maxReconnectDelayMsSinceLast: number
  maxResubscribeDelayMsSinceLast: number
  maxResubscribeChannelsSinceLast: number
  droppedQueueItemsSinceLast: number
  coalescedQueueItemsSinceLast: number
  tickerBufferFlushesSinceLast: number
  maxTickerBufferedSymbolsSinceLast: number
  maxTickerFlushMsSinceLast: number
}

const state = {
  channels: {} as Record<string, ChannelStat>,
  bytesTotal: 0,
  framesTotal: 0,
  bulkFramesTotal: 0,
  criticalFramesTotal: 0,
  bulkBytesTotal: 0,
  criticalBytesTotal: 0,
  decodeMsTotal: 0,
  dispatchMsTotal: 0,
  startedAtMs: Date.now(),
  samples: [] as SessionSample[],
  longTaskMsAccum: 0,
  lastSampleFrames: 0,
  lastSampleBulkFrames: 0,
  lastSampleCriticalFrames: 0,
  lastSampleBulkBytes: 0,
  lastSampleCriticalBytes: 0,
  lastSampleBytes: 0,
  lastSampleDecodeMs: 0,
  lastSampleDispatchMs: 0,
  maxQueueLagMsSinceLast: 0,
  maxSourceAgeMsSinceLast: 0,
  reconnectScheduledSinceLast: 0,
  resubscribeScheduledSinceLast: 0,
  resubscribeRunsSinceLast: 0,
  serverErrorsSinceLast: 0,
  maxReconnectDelayMsSinceLast: 0,
  maxResubscribeDelayMsSinceLast: 0,
  maxResubscribeChannelsSinceLast: 0,
  droppedQueueItemsSinceLast: 0,
  coalescedQueueItemsSinceLast: 0,
  tickerBufferFlushesSinceLast: 0,
  maxTickerBufferedSymbolsSinceLast: 0,
  maxTickerFlushMsSinceLast: 0,
}

type FrameTiming = {
  queueLagMs?: number | null
  sourceAgeMs?: number | null
  transportLane?: 'bulk' | 'critical'
}

export type StreamPerformanceEvent =
  | { type: 'reconnect-scheduled'; delayMs: number }
  | { type: 'resubscribe-scheduled'; delayMs: number }
  | { type: 'resubscribe-run'; channelCount: number }
  | { type: 'queue-items-dropped'; count: number }
  | { type: 'queue-items-coalesced'; count: number }
  | { type: 'ticker-buffer-flush'; bufferedSymbols: number; durationMs: number }
  | { type: 'server-error' }

export function isStreamDebugEnabled(): boolean {
  // Matches the SSR guard used elsewhere in this codebase (see
  // lib/safe-storage.ts) rather than a bare globalThis.localStorage check.
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem('oemsStreamDebug') === '1'
  } catch {
    // Private mode or blocked storage — instrumentation is optional.
    return false
  }
}

/**
 * Tallies one inbound wire frame. Cheap by construction (a handful of adds and
 * a loop over the frame's own channel keys, usually one or two) — the caller
 * is expected to guard the whole call behind the flag it read at mount, so a
 * disabled session does not even reach this function.
 */
export function recordFrame(
  channelKeys: readonly string[],
  byteLength: number,
  decodeMs: number,
  dispatchMs: number,
  timing: FrameTiming = {}
): void {
  state.bytesTotal += byteLength
  state.framesTotal += 1
  state.decodeMsTotal += decodeMs
  state.dispatchMsTotal += dispatchMs
  if (timing.transportLane === 'bulk') {
    state.bulkFramesTotal += 1
    state.bulkBytesTotal += byteLength
  } else if (timing.transportLane === 'critical') {
    state.criticalFramesTotal += 1
    state.criticalBytesTotal += byteLength
  }
  if (timing.queueLagMs !== null && timing.queueLagMs !== undefined) {
    state.maxQueueLagMsSinceLast = Math.max(state.maxQueueLagMsSinceLast, timing.queueLagMs)
  }
  if (timing.sourceAgeMs !== null && timing.sourceAgeMs !== undefined) {
    state.maxSourceAgeMsSinceLast = Math.max(state.maxSourceAgeMsSinceLast, timing.sourceAgeMs)
  }

  // A frame routed to several keys is attributed in full to each: the question
  // is "what would removing this stream save", and per-key shares would not
  // answer it. `bytesTotal` stays the honest total of the frames this function
  // sees — data frames only, since the caller returns early on ping/auth/error.
  //
  // A data frame `extractChannelKeys` has no case for (the Amberdata arms it
  // omits, or a top-level `ServerMessage.msg` arm) routes to nothing, so
  // without this bucket its bytes would land in `bytesTotal` and in no channel
  // — invisible volume in the one report meant to find it.
  for (const key of channelKeys.length > 0 ? channelKeys : [UNROUTED_CHANNEL]) {
    const entry = state.channels[key] ?? { bytes: 0, frames: 0 }
    entry.bytes += byteLength
    entry.frames += 1
    state.channels[key] = entry
  }
}

export function recordStreamPerformanceEvent(event: StreamPerformanceEvent): void {
  switch (event.type) {
    case 'reconnect-scheduled':
      state.reconnectScheduledSinceLast += 1
      state.maxReconnectDelayMsSinceLast = Math.max(
        state.maxReconnectDelayMsSinceLast,
        event.delayMs
      )
      return
    case 'resubscribe-scheduled':
      state.resubscribeScheduledSinceLast += 1
      state.maxResubscribeDelayMsSinceLast = Math.max(
        state.maxResubscribeDelayMsSinceLast,
        event.delayMs
      )
      return
    case 'resubscribe-run':
      state.resubscribeRunsSinceLast += 1
      state.maxResubscribeChannelsSinceLast = Math.max(
        state.maxResubscribeChannelsSinceLast,
        event.channelCount
      )
      return
    case 'queue-items-dropped':
      state.droppedQueueItemsSinceLast += event.count
      return
    case 'queue-items-coalesced':
      state.coalescedQueueItemsSinceLast += event.count
      return
    case 'ticker-buffer-flush':
      state.tickerBufferFlushesSinceLast += 1
      state.maxTickerBufferedSymbolsSinceLast = Math.max(
        state.maxTickerBufferedSymbolsSinceLast,
        event.bufferedSymbols
      )
      state.maxTickerFlushMsSinceLast = Math.max(state.maxTickerFlushMsSinceLast, event.durationMs)
      return
    case 'server-error':
      state.serverErrorsSinceLast += 1
  }
}

type IntervalScheduler = {
  setInterval: (callback: () => void, delayMs: number) => ReturnType<typeof setInterval>
  clearInterval: (id: ReturnType<typeof setInterval>) => void
}

const defaultScheduler: IntervalScheduler = {
  setInterval: (callback, delayMs) => setInterval(callback, delayMs),
  clearInterval: (id) => clearInterval(id),
}

type LongTaskObserverFactory = (
  onEntries: (durationsMs: number[]) => void
) => { disconnect: () => void } | undefined

// Real PerformanceObserver wiring, isolated behind the same factory shape a
// test double implements — durations are extracted here so callers (and
// tests) never need to construct a real PerformanceObserverEntryList.
const defaultCreateLongTaskObserver: LongTaskObserverFactory = (onEntries) => {
  try {
    const observer = new PerformanceObserver((list) => {
      onEntries(list.getEntries().map((entry) => entry.duration))
    })
    observer.observe({ entryTypes: ['longtask'] })
    return observer
  } catch {
    // Not supported in this browser; heap and frame counts still sample.
    return undefined
  }
}

/**
 * Samples session-age-correlated cost every 60s. This is the premise check:
 * flat decode time against rising long-task time means the lag is
 * accumulation, not volume.
 *
 * `enabled` is passed in rather than read here so the caller reads the flag
 * exactly once per mount and can reuse that answer on the per-frame path.
 * No-ops (and returns a no-op cleanup) when it is false.
 *
 * `scheduler` and `createLongTaskObserver` default to the real globals — they
 * exist as parameters solely so tests can drive ticks and long-task entries
 * deterministically instead of waiting on a real 60s timer or a real
 * browser-reported long task. No production call site needs to pass them.
 */
export function startSessionSampler(
  enabled: boolean,
  getListenerCount: () => number,
  scheduler: IntervalScheduler = defaultScheduler,
  createLongTaskObserver: LongTaskObserverFactory = defaultCreateLongTaskObserver,
  onSample: (sample: SessionSample) => void = () => {}
): () => void {
  if (!enabled) return () => {}

  // Console reach-in. The module is bundled, so `getStreamStats` has no name a
  // devtools console can resolve; Step 3 of the baseline capture reads the run
  // off `window.getStreamStats()`. Flag-gated and removed on teardown, so it
  // never exists in a normal session.
  if (typeof window !== 'undefined') {
    ;(window as unknown as { getStreamStats?: typeof getStreamStats }).getStreamStats =
      getStreamStats
  }

  const observer = createLongTaskObserver((durationsMs) => {
    for (const durationMs of durationsMs) state.longTaskMsAccum += durationMs
  })

  const timer = scheduler.setInterval(() => {
    const perf = performance as Performance & { memory?: { usedJSHeapSize: number } }
    const sample: SessionSample = {
      atMs: Date.now(),
      ageMs: Date.now() - state.startedAtMs,
      bytesSinceLast: state.bytesTotal - state.lastSampleBytes,
      dispatchMsSinceLast: state.dispatchMsTotal - state.lastSampleDispatchMs,
      heapBytes: perf.memory?.usedJSHeapSize ?? null,
      longTaskMsSinceLast: state.longTaskMsAccum,
      listenerCount: getListenerCount(),
      framesSinceLast: state.framesTotal - state.lastSampleFrames,
      bulkFramesSinceLast: state.bulkFramesTotal - state.lastSampleBulkFrames,
      criticalFramesSinceLast: state.criticalFramesTotal - state.lastSampleCriticalFrames,
      bulkBytesSinceLast: state.bulkBytesTotal - state.lastSampleBulkBytes,
      criticalBytesSinceLast: state.criticalBytesTotal - state.lastSampleCriticalBytes,
      decodeMsSinceLast: state.decodeMsTotal - state.lastSampleDecodeMs,
      maxQueueLagMsSinceLast: state.maxQueueLagMsSinceLast,
      maxSourceAgeMsSinceLast: state.maxSourceAgeMsSinceLast,
      reconnectScheduledSinceLast: state.reconnectScheduledSinceLast,
      resubscribeScheduledSinceLast: state.resubscribeScheduledSinceLast,
      resubscribeRunsSinceLast: state.resubscribeRunsSinceLast,
      serverErrorsSinceLast: state.serverErrorsSinceLast,
      maxReconnectDelayMsSinceLast: state.maxReconnectDelayMsSinceLast,
      maxResubscribeDelayMsSinceLast: state.maxResubscribeDelayMsSinceLast,
      maxResubscribeChannelsSinceLast: state.maxResubscribeChannelsSinceLast,
      droppedQueueItemsSinceLast: state.droppedQueueItemsSinceLast,
      coalescedQueueItemsSinceLast: state.coalescedQueueItemsSinceLast,
      tickerBufferFlushesSinceLast: state.tickerBufferFlushesSinceLast,
      maxTickerBufferedSymbolsSinceLast: state.maxTickerBufferedSymbolsSinceLast,
      maxTickerFlushMsSinceLast: state.maxTickerFlushMsSinceLast,
    }
    state.samples.push(sample)
    if (state.samples.length > MAX_SESSION_SAMPLES) state.samples.shift()
    onSample(sample)
    state.longTaskMsAccum = 0
    state.lastSampleFrames = state.framesTotal
    state.lastSampleBulkFrames = state.bulkFramesTotal
    state.lastSampleCriticalFrames = state.criticalFramesTotal
    state.lastSampleBulkBytes = state.bulkBytesTotal
    state.lastSampleCriticalBytes = state.criticalBytesTotal
    state.lastSampleBytes = state.bytesTotal
    state.lastSampleDecodeMs = state.decodeMsTotal
    state.lastSampleDispatchMs = state.dispatchMsTotal
    state.maxQueueLagMsSinceLast = 0
    state.maxSourceAgeMsSinceLast = 0
    state.reconnectScheduledSinceLast = 0
    state.resubscribeScheduledSinceLast = 0
    state.resubscribeRunsSinceLast = 0
    state.serverErrorsSinceLast = 0
    state.maxReconnectDelayMsSinceLast = 0
    state.maxResubscribeDelayMsSinceLast = 0
    state.maxResubscribeChannelsSinceLast = 0
    state.droppedQueueItemsSinceLast = 0
    state.coalescedQueueItemsSinceLast = 0
    state.tickerBufferFlushesSinceLast = 0
    state.maxTickerBufferedSymbolsSinceLast = 0
    state.maxTickerFlushMsSinceLast = 0
  }, 60_000)

  return () => {
    scheduler.clearInterval(timer)
    observer?.disconnect()
    if (typeof window !== 'undefined') {
      delete (window as unknown as { getStreamStats?: typeof getStreamStats }).getStreamStats
    }
  }
}

export function getStreamStats() {
  return {
    channels: state.channels,
    bytesTotal: state.bytesTotal,
    framesTotal: state.framesTotal,
    decodeMsTotal: state.decodeMsTotal,
    dispatchMsTotal: state.dispatchMsTotal,
    sessionAgeMs: Date.now() - state.startedAtMs,
    samples: state.samples,
  }
}

/** Test-only. */
export function __resetStreamStats(): void {
  state.channels = {}
  state.bytesTotal = 0
  state.framesTotal = 0
  state.bulkFramesTotal = 0
  state.criticalFramesTotal = 0
  state.bulkBytesTotal = 0
  state.criticalBytesTotal = 0
  state.decodeMsTotal = 0
  state.dispatchMsTotal = 0
  state.startedAtMs = Date.now()
  state.samples = []
  state.longTaskMsAccum = 0
  state.lastSampleFrames = 0
  state.lastSampleBulkFrames = 0
  state.lastSampleCriticalFrames = 0
  state.lastSampleBulkBytes = 0
  state.lastSampleCriticalBytes = 0
  state.lastSampleBytes = 0
  state.lastSampleDecodeMs = 0
  state.lastSampleDispatchMs = 0
  state.maxQueueLagMsSinceLast = 0
  state.maxSourceAgeMsSinceLast = 0
  state.reconnectScheduledSinceLast = 0
  state.resubscribeScheduledSinceLast = 0
  state.resubscribeRunsSinceLast = 0
  state.serverErrorsSinceLast = 0
  state.maxReconnectDelayMsSinceLast = 0
  state.maxResubscribeDelayMsSinceLast = 0
  state.maxResubscribeChannelsSinceLast = 0
  state.droppedQueueItemsSinceLast = 0
  state.coalescedQueueItemsSinceLast = 0
  state.tickerBufferFlushesSinceLast = 0
  state.maxTickerBufferedSymbolsSinceLast = 0
  state.maxTickerFlushMsSinceLast = 0
}
